import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { SignedMedia } from "@/lib/signedFile";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, ChevronLeft, Video, FileText, Link2,
  BookOpen, ClipboardCheck, Layers, FileQuestion, Save, X, PenLine,
} from "lucide-react";

const MATERIAL_TYPES = [
  { value: "video", label: "فيديو", icon: Video },
  { value: "pdf", label: "PDF", icon: FileText },
  { value: "text", label: "نص", icon: FileText },
  { value: "link", label: "رابط", icon: Link2 },
];

export default function CourseBuilder() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [savingSection, setSavingSection] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/courses/${id}`);
      setCourse(data.course);
      setSections(data.sections || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addSection = async () => {
    if (!newSectionTitle.trim()) { toast.error("عنوان القسم مطلوب"); return; }
    setSavingSection(true);
    try {
      await api.post(`/courses/${id}/sections`, { title: newSectionTitle.trim(), order: sections.length });
      toast.success("تم إضافة القسم");
      setNewSectionTitle("");
      setShowSectionForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSection(false); }
  };

  const removeSection = async (sid) => {
    if (!await appConfirm("حذف القسم مع محتواه؟")) return;
    try {
      await api.delete(`/sections/${sid}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMaterial = async (mid) => {
    if (!await appConfirm("حذف المادة؟")) return;
    try {
      await api.delete(`/materials/${mid}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !course) {
    return <DashboardLayout><div className="grid place-items-center py-32"><Loader2 className="h-10 w-10 animate-spin text-teal-700" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div data-testid="course-builder">
        <Link to="/dashboard/courses" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-700 mb-4">
          <ChevronLeft className="h-4 w-4" />
          العودة للمقررات
        </Link>

        <div className="mb-8 p-5 rounded-3xl bg-gradient-to-br from-teal-700 to-teal-900 text-white relative overflow-hidden">
          <div className="absolute -end-16 -top-16 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl" />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-2">وحدة التعلّم الإلكتروني</div>
            <h1 className="text-3xl lg:text-4xl font-heading font-black leading-tight">{course.title}</h1>
            {course.program_name && <div className="mt-2 text-teal-100 text-sm">{course.program_name}</div>}
            {course.description && <p className="mt-4 text-teal-50/90 max-w-3xl leading-relaxed">{course.description}</p>}
            <div className="mt-6 flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                course.status === "published" ? "bg-emerald-500/20 text-emerald-100" : "bg-white/10 text-white"
              }`}>
                {course.status === "published" ? "منشور" : "مسودة"}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white inline-flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" /> {sections.length} قسم
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-bold text-slate-900">أقسام المقرر</h2>
          <button onClick={() => setShowSectionForm(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600" data-testid="add-section-btn">
            <Plus className="h-4 w-4" /> قسم جديد
          </button>
        </div>

        {showSectionForm && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setShowSectionForm(false)}>
            <div className="w-full max-w-md bg-white rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="section-form">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-heading font-black text-slate-900">قسم جديد</h3>
                <button onClick={() => setShowSectionForm(false)} className="p-2 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
              </div>
              <label className="block text-sm font-medium text-slate-700 mb-2">عنوان القسم</label>
              <input
                autoFocus
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addSection()}
                placeholder="مثال: مقدمة إلى البرمجة"
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                data-testid="section-title-input"
              />
              <div className="mt-6 flex gap-3">
                <button onClick={() => setShowSectionForm(false)} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
                <button onClick={addSection} disabled={savingSection} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-section-btn">
                  {savingSection ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  إضافة
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {sections.length === 0 && (
            <div className="p-12 rounded-2xl bg-white border border-slate-200 text-center">
              <Layers className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <div className="text-slate-600">ابدأ بإضافة أول قسم للمقرر</div>
            </div>
          )}
          {sections.map((s, idx) => (
            <SectionCard key={s.id} section={s} idx={idx} onDelete={() => removeSection(s.id)} onRefresh={load} onRemoveMaterial={removeMaterial} />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

function SectionCard({ section, idx, onDelete, onRefresh, onRemoveMaterial }) {
  const [showMat, setShowMat] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`section-${section.id}`}>
      <div className="p-4 flex items-center justify-between border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-teal-700 text-white grid place-items-center font-heading font-black arabic-numerals">
            {String(idx + 1).padStart(2, "0")}
          </div>
          <div>
            <div className="font-heading font-bold text-slate-900">{section.title}</div>
            <div className="text-xs text-slate-500 mt-0.5 arabic-numerals">
              {(section.materials || []).length} مادة {section.quiz ? "· اختبار" : ""}
            </div>
          </div>
        </div>
        <button onClick={onDelete} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-section-${section.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-2">
        {(section.materials || []).map((m) => {
          const T = MATERIAL_TYPES.find((t) => t.value === m.type) || MATERIAL_TYPES[0];
          return (
            <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-teal-200 hover:bg-slate-50/50 group" data-testid={`material-${m.id}`}>
              <div className="h-9 w-9 rounded-lg bg-teal-50 text-teal-700 grid place-items-center shrink-0">
                <T.icon className="h-4 w-4" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 text-sm truncate">{m.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{T.label}{m.duration_minutes ? ` · ${m.duration_minutes} دقيقة` : ""}</div>
              </div>
              <button onClick={() => onRemoveMaterial(m.id)} className="opacity-0 group-hover:opacity-100 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-opacity">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}

        {section.quiz && (
          <div className="mt-3 p-4 rounded-lg border-2 border-amber-200 bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-amber-500 text-white grid place-items-center shrink-0">
                <ClipboardCheck className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-slate-900 text-sm">{section.quiz.title}</div>
                <div className="text-xs text-slate-600 mt-0.5 arabic-numerals">
                  {(section.quiz.questions || []).length} سؤال · درجة النجاح {section.quiz.passing_score}%
                </div>
              </div>
              <button onClick={() => setShowQuiz(true)} className="text-xs font-semibold text-amber-800 hover:underline">
                <PenLine className="h-4 w-4 inline" /> تعديل
              </button>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => setShowMat(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200" data-testid={`add-material-${section.id}`}>
            <Plus className="h-4 w-4" /> إضافة مادة
          </button>
          <button onClick={() => setShowQuiz(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-100 text-amber-800 text-sm font-medium hover:bg-amber-200" data-testid={`add-quiz-${section.id}`}>
            <FileQuestion className="h-4 w-4" /> {section.quiz ? "تعديل الاختبار" : "إضافة اختبار"}
          </button>
        </div>
      </div>

      {showMat && <AddMaterialModal sectionId={section.id} courseId={section.course_id} onClose={() => setShowMat(false)} onSaved={() => { setShowMat(false); onRefresh(); }} />}
      {showQuiz && <QuizModal sectionId={section.id} existing={section.quiz} onClose={() => setShowQuiz(false)} onSaved={() => { setShowQuiz(false); onRefresh(); }} />}
    </div>
  );
}

function AddMaterialModal({ sectionId, courseId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", type: "video", content_url: "", text_content: "", duration_minutes: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("context", "course_material");
      if (courseId) fd.append("course_id", courseId);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, content_url: data.file_id, title: f.title || data.original_filename }));
      toast.success("تم رفع الملف بنجاح");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title) { toast.error("العنوان مطلوب"); return; }
    setSaving(true);
    try {
      await api.post(`/sections/${sectionId}/materials`, {
        ...form,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
      });
      toast.success("تم إضافة المادة");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="material-form">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-heading font-black text-slate-900">إضافة مادة</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">النوع</label>
            <div className="grid grid-cols-4 gap-2">
              {MATERIAL_TYPES.map((t) => (
                <button key={t.value} type="button" onClick={() => setForm({ ...form, type: t.value })} className={`p-3 rounded-lg border text-xs font-semibold flex flex-col items-center gap-1 transition-all ${
                  form.type === t.value ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-700 border-slate-200 hover:border-teal-300"
                }`}>
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">عنوان المادة</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="mat-title" />
          </div>

          {form.type === "text" ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">المحتوى النصي</label>
              <textarea rows={5} value={form.text_content} onChange={(e) => setForm({ ...form, text_content: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="mat-text" />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">الرابط أو ارفع ملفاً</label>
              <input value={form.content_url} onChange={(e) => setForm({ ...form, content_url: e.target.value })} placeholder="https://..." dir="ltr" className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="mat-url" />
              <div className="mt-2 flex items-center gap-2">
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cream-100 text-teal-800 text-sm font-semibold cursor-pointer hover:bg-cream-200 transition-colors">
                  <input type="file" onChange={uploadFile} className="hidden" data-testid="upload-input" accept={form.type === "video" ? "video/mp4,video/webm" : form.type === "pdf" ? "application/pdf" : "application/pdf,image/jpeg,image/png,image/webp"} />
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "📎"}
                  {uploading ? "جاري الرفع..." : "رفع من جهازك"}
                </label>
                <p className="text-xs text-slate-500">أو الصق رابطاً خارجياً (YouTube، رابط PDF...)</p>
              </div>
            </div>
          )}

          {form.type === "video" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">المدة (بالدقائق)</label>
              <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" />
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
          <button onClick={save} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-mat-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            حفظ
          </button>
        </div>
      </div>
    </div>
  );
}

function QuizModal({ sectionId, existing, onClose, onSaved }) {
  const [title, setTitle] = useState(existing?.title || "اختبار القسم");
  const [passing, setPassing] = useState(existing?.passing_score || 60);
  const [questions, setQuestions] = useState(
    (existing?.questions || []).map((q) => ({
      text: q.text,
      options: q.options || ["", "", "", ""],
      correct_index: q.correct_index ?? 0,
    }))
  );
  const [saving, setSaving] = useState(false);

  const addQ = () => setQuestions([...questions, { text: "", options: ["", "", "", ""], correct_index: 0 }]);
  const updateQ = (idx, patch) => setQuestions(questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  const removeQ = (idx) => setQuestions(questions.filter((_, i) => i !== idx));

  const save = async () => {
    if (questions.length === 0) { toast.error("أضف سؤالاً واحداً على الأقل"); return; }
    for (const q of questions) {
      if (!q.text) { toast.error("كل سؤال يحتاج نصاً"); return; }
      const opts = q.options.filter((o) => o.trim());
      if (opts.length < 2) { toast.error("كل سؤال يحتاج خيارين على الأقل"); return; }
    }
    setSaving(true);
    try {
      const clean = questions.map((q) => ({
        text: q.text,
        options: q.options.filter((o) => o.trim()),
        correct_index: Math.min(q.correct_index, q.options.filter((o) => o.trim()).length - 1),
      }));
      await api.post(`/sections/${sectionId}/quiz`, { title, passing_score: parseInt(passing) || 60, order: 0, questions: clean });
      toast.success("تم حفظ الاختبار");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-start pt-8 pb-8 overflow-y-auto p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl p-5 my-auto" onClick={(e) => e.stopPropagation()} data-testid="quiz-form">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-heading font-black text-slate-900">إعداد الاختبار</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">عنوان الاختبار</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">درجة النجاح %</label>
            <input type="number" value={passing} onChange={(e) => setPassing(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none arabic-numerals" />
          </div>
        </div>

        <div className="space-y-4 max-h-[45vh] overflow-y-auto pe-2">
          {questions.map((q, qi) => (
            <div key={qi} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-slate-500 arabic-numerals">السؤال {qi + 1}</div>
                <button onClick={() => removeQ(qi)} className="p-1.5 rounded text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <textarea rows={2} value={q.text} onChange={(e) => updateQ(qi, { text: e.target.value })} placeholder="نص السؤال" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none mb-3" />
              <div className="space-y-2">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <button onClick={() => updateQ(qi, { correct_index: oi })} className={`h-6 w-6 rounded-full border-2 shrink-0 grid place-items-center ${
                      q.correct_index === oi ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300"
                    }`}>
                      {q.correct_index === oi && <span className="text-xs">✓</span>}
                    </button>
                    <input value={opt} onChange={(e) => {
                      const newOpts = [...q.options]; newOpts[oi] = e.target.value; updateQ(qi, { options: newOpts });
                    }} placeholder={`الخيار ${oi + 1}`} className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 outline-none" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button onClick={addQ} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-slate-300 text-slate-600 text-sm font-semibold hover:border-teal-400 hover:text-teal-700 transition-colors">
            <Plus className="h-4 w-4" /> إضافة سؤال
          </button>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100">إلغاء</button>
          <button onClick={save} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-quiz-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            حفظ الاختبار
          </button>
        </div>
      </div>
    </div>
  );
}
